import { MouseEvent, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { path, UserInfo as UserInfoType } from "../common/Common";
import UserInfo from "./UserInfo";
import PasswordForm, { PasswordFormType } from "./PasswordForm";
import DataProvider from "../../data_provider/DataProvider";
import { PasswordQueryParams, UserInfoReqBody } from "../../data_provider/gateways/UserGateway";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import { encodeUnicode } from "../common/Common";

export interface MembershipInfo {
    label: string;
    subscribe: boolean;
    msg: string;
    callback: (e: MouseEvent<HTMLElement>) => void;
    timeLeft?: number;
}

/**
 * @author bakim
 * @date 2024-09-11 10:56:38
 * @remark 내 정보 영역
 * @returns <UserInfoArea>
 */

const UserInfoArea = () => {
    const { ModalComponent, openModal, closeModal } = useModal();

    const navigate = useNavigate();
    const location = useLocation();

    const [userInfo, setUserInfo] = useState<UserInfoType>();
    const [editMode, setEditMode] = useState<string | undefined>(undefined);

    // 마이페이지인지 확인
    const isMyPageByMatch = !!useMatch({ path: path.MYPAGE, end: false }); // 앞부분 맞으면 매칭( /mypage/*)
    const isMypageByPath = location.pathname === path.MYPAGE || location.pathname.startsWith(`${path.MYPAGE}/`);
    const isMyPage = isMyPageByMatch || isMypageByPath;

    useEffect(() => {
        getUserInfo();
        return () => {
            setEditMode(undefined);
            setUserInfo(undefined);
        };
    }, []);

    const showModal = (message: React.ReactNode) => {
        openModal({
            title: "오류",
            children: message,
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Danger",
                            onClick: () => closeModal(),
                        },
                    ]}
                />
            ),
        });
    };

    /**
     * 사용자 정보 조회
     * @returns UserInfo 사용자 정보
     */
    const getUserInfo = async () => {
        // DataProvider.GetUserInfo().then((response) => {
        //     response.flag && setUserInfo(response.result);
        // });

        // hmchoi 오류별 처리 구현
        const response = await DataProvider.GetUserInfo();

        if (!response.flag) {
            switch (response.status) {
                case 400:
                    showModal("사용자 정보 요청이 올바르지 않습니다."); break;
                case 404:
                    showModal("사용자 정보를 찾을 수 없습니다."); break;
                case 500:
                    showModal("사용자 정보를 불러오는 중 오류가 발생했습니다.."); break;
                default:
                    showModal("일시적인 오류가 발생했습니다."); break;
            }

            return;
        }

        setUserInfo(response.result);
    };

    /**
     * 비밀번호 확인이 필요한 정보 변경 시 진행하는 컴포넌트 전환 용 수정 모드 설정 함수
     */
    const handleEditMode = (editMode: string) => {
        setEditMode(editMode);
    };

    /**
     * 비밀번호 확인 컴포넌트 종료 함수
     */
    const onDismiss = () => {
        setEditMode(undefined);

        // 마이페이지 인 경우 페이지 이동 하지 않고(컴포넌트만 교체됨),
        // 마이페이지가 아닌 경우에만 홈으로 보내도록 분기 | 250902 | smyun
        // TODO: [아이디/비밀번호찾기] 메일에서 비밀번호 변경페이지로 접근하는 경우 Main의 라우트 대신 이 컴포넌트를 사용하는 것으로 변경되면 아래 코드 사용될 것으로 예상
        if (!isMyPage) {
            // 히스토리 남기지 않게 기록 교체 replace true 처리
            navigate(path.ROOT, { replace: true });
        }
    };

    /**
     * 비밀번호 변경
     */
    const editPassword = (data: PasswordQueryParams) => {
        return DataProvider.UpdatePassword(data);
    };

    /**
     * 회원탈퇴
     */
    const withdraw = (data: PasswordQueryParams) => {
        return DataProvider.DeleteUser(data);
    };

    /**
     * 사용자 정보 변경
     */
    const [cookies, setCookies, removeCookies] = useCookies(["KMUN"]);
    const editUserInfo = (formData: FormData) => {
        let data = {} as UserInfoReqBody;
        data = Object.assign(data, Object.fromEntries(formData.entries()));
        Object.values(data).filter((value) => value).length &&
            DataProvider.UpdateUserInfo(data).then((response) => {
                if (response.flag) {
                    // 250908 | smyun | stale 값 이용하지 않도록 prev로 수정
                    setUserInfo((prev) => ({
                        ...prev!,
                        username: data.username ?? prev!.username,
                        emailNotification: data.emailNotification ?? prev!.emailNotification,
                    }));

                    // 25.08.28 | Kim Young Jun | username이 변경된 경우에만 쿠키 업데이트
                    if (data.username) {
                        // setCookies("KMUN", encodeUnicode(data.username));

                        // 250908 | smyun | 백엔드와 쿠키 도메인이 달라 갱신되지 않으므로 도메인 부 수정
                        setCookies("KMUN", encodeUnicode(data.username!), {
                            path: "/",
                            domain: getCookieDomain, // ★ 중요
                            sameSite: "lax",
                            secure: true, // https라면 권장
                        });

                        // 닉네임 변경 시 탭간 동기화 bc 추가 | smyun | 250908
                        const bc = new BroadcastChannel("auth-channel");
                        bc.postMessage({ type: "nickname-updated", nickname: data.username });
                    }
                }

                // TODO 에러 별 화면 전환
            });
    };

    /* 도메인 얻기*/
    const getCookieDomain = getBaseDomain(window.location.hostname);
    function getBaseDomain(host: string) {
        //로컬인 경우
        if (/^30\.30\.30\./.test(host)) {
            return undefined; // domain 지정하지 않음 → host-only 쿠키
        }

        const parts = host.split(".");
        if (parts.length >= 2) {
            return "." + parts.slice(-2).join("."); // ".modoomap.com"
        }
        return host;
    }

    /**
     * 수정 모드 별 비밀번호 확인 컴포넌트 요청 함수
     * @return Component
     */
    const getEditComponent = () => {
        switch (editMode) {
            case "password":
                return (
                    <PasswordForm
                        title="비밀번호 변경"
                        mode={PasswordFormType.Change}
                        data={userInfo!}
                        onSubmit={editPassword}
                        onDismiss={onDismiss}
                    />
                );
            case "withdraw":
                return <PasswordForm title="회원탈퇴" mode={PasswordFormType.Withdrawn} data={userInfo!} onSubmit={withdraw} onDismiss={onDismiss} />;
            default:
                return <></>;
        }
    };

    /**
     * 사용자 정보 중 멤버십 관련 정보 데이터 재구성 함수
     * @return MembershipInfo
     */
    const getMembershipInfo = () => {
        const membership = { ...userInfo!.membership };

        // 기본 정보 설정(일반사용자 기준)
        const info: MembershipInfo = {
            label: "가입하기",
            subscribe: false,
            msg: "더 많은 기능을 사용할 수 있는 모두의지도 PRO를 사용해보세요!",
            timeLeft: 0,
            callback: function (e: MouseEvent<HTMLElement>): void {
                // 모두의지도 가입 페이지로 이동
                navigate(path.PRICE_POLICY);
            },
        };

        if (membership.gradeCode === 0) {
            // 관리자 계정
            return {} as MembershipInfo;
        } else if (membership.gradeCode === 1) {
            // 일반 사용자
            return info;
        } else {
            // 모두의지도 프로 사용자

            // 남은 일자 계산
            info.timeLeft = membership.expiredDate ? new Date(membership.expiredDate).getTime() - Date.now() : Infinity;

            // 남은 일자 기준 구독 여부 확인(다음달 결제에 대한 취소를 하지 않은 경우)
            info.subscribe = info.timeLeft > 0;

            if (!info.subscribe) return info;

            // 곧 종료 예정인 경우
            if (membership.expiredDate) {
                info.msg = `${membership?.expiredDate}에 PRO 혜택이 종료됩니다.`;
                info.label = "연장하기";
                info.callback = () => {
                    openModal({
                        title: "안내",
                        children: "연장하기",
                        modalButtons: (
                            <ModalButtons
                                buttonCount={1}
                                buttons={[
                                    {
                                        label: "확인",
                                        color: "Primary",
                                        onClick: async () => {
                                            // hmchoi TODO 재결제 API 추가 필요
                                            // const result = await DataProvider.Resubscribe();
                                            closeModal();
                                        },
                                    },
                                ]}
                            />
                        ),
                    });
                };
            } else {
                // 구독 중인 사용자인 경우
                const withDate = membership.startDate
                    ? Math.ceil((Date.now() - new Date(membership.startDate).getTime()) / (60 * 60 * 24 * 1000))
                    : undefined;
                info.label = "";
                info.msg = withDate ? `${withDate} 일 동안 모두의지도 ${membership.grade}와 함께하는 중` : "";
            }
        }

        return info;
    };

    return (
        <>
            <ModalComponent />
            {!editMode && userInfo ? (
                <UserInfo data={userInfo} handleEditMode={handleEditMode} membershipInfo={getMembershipInfo()} editUserInfo={editUserInfo} />
            ) : (
                <>{userInfo && getEditComponent()}</>
            )}
        </>
    );
};

export default UserInfoArea;
